import {
  Delete as DeleteIcon,
  ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { map } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { usersApi } from 'api';
import {
  CloseDialogResult,
  CopyText,
  CrudPage,
  DataGridColumnDefinition,
  Dialog,
  MailLink,
  RoleLabel,
  StylizedNumber,
  UserStatusLabel,
} from 'components';
import { ROUTE_PATH } from 'constants/routes';
import { TIME_INTERVAL } from 'constants/time-interval.constants';
import { FilterDefinitionType, QueryKey, UserRole, UserStatus } from 'enums';
import { useMutation, usePartialQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { AgentInfo, DateFilters, FilterDefinition, User } from 'types';
import { authUtils, userUtils } from 'utils';

type UsersFilters = {
  userId: string;
  role: UserRole;
  status: UserStatus;
  agentPayinCompensation: number;
  agentPayoutCompensation: number;
  agentId: string;
} & DateFilters;

export const UserList: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.users.user_list',
  });
  const { t: tCommon } = useTranslation();

  const { isAdmin, isTechOperator } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const queryResultAllUsers = useQuery(QueryKey.Users, usersApi.getAll);

  const queryResultFilteredUsers = usePartialQuery(
    QueryKey.Users,
    usersApi.getAllFilteredAndPaginated,
    {
      refetchInterval: TIME_INTERVAL.ONE_MINUTE,
      refetchIntervalInBackground: true,
    },
  );

  const queryResultAgents = useQuery(QueryKey.Agents, usersApi.getAllAgents);

  const agents = useMemo(
    () => queryResultAgents.data || [],
    [queryResultAgents.data],
  );

  const [confirmRemoveDialogProps, setConfirmRemoveDialogProps] = useState<{
    open: boolean;
    data?: User;
  }>({
    open: false,
  });

  const { mutate: remove } = useMutation(usersApi.remove, {
    onSuccess: () => {
      setConfirmRemoveDialogProps({ open: false });
      queryClient.invalidateQueries(QueryKey.Users);
      queryClient.invalidateQueries(QueryKey.UsersPaginated);
      queryClient.invalidateQueries(QueryKey.UsersTraders);
    },
    notifierType: 'remove',
  });

  const userEmail = useMemo(() => authUtils.getUserEmail(), []);

  const getAgentName = useCallback(
    (agent: Partial<User>) => agent.name || agent.email,
    [],
  );

  const isActionDisabled = useCallback(
    (item: User) => !isAdmin && item.role === UserRole.Admin,
    [isAdmin],
  );

  const handleRemove = useCallback((user: User) => {
    setConfirmRemoveDialogProps({ open: true, data: user });
  }, []);

  const userEditPath = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.USER_DETAILS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.USER_DETAILS;
    }
  }, [isAdmin, isTechOperator]);

  const settingsPath = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.SETTINGS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.SETTINGS;
    }
  }, [isAdmin, isTechOperator]);

  const handleEdit = useCallback(
    (user: User) => {
      let path = null;
      if (user.email === userEmail && settingsPath) {
        path = settingsPath;
      } else if (userEditPath) {
        path = generatePath(userEditPath, { id: user.id });
      }

      path && navigate(path);
    },
    [userEmail, userEditPath, settingsPath, navigate],
  );

  const handleCloseRemoveDialog = useCallback(
    ({ ok, data }: CloseDialogResult<User>) => {
      if (ok && data) {
        remove(data);
      } else {
        setConfirmRemoveDialogProps({ open: false });
      }
    },
    [remove],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<User>[] => [
      {
        header: t('table.columns.id'),
        valueGetter: (item) => <CopyText iconOnly text={item.id} />,
      },
      {
        header: t('table.columns.name'),
        valueGetter: (item) => (
          <>
            <div>{item.name}</div>
            <div>
              <MailLink email={item.email} />
            </div>
          </>
        ),
      },
      {
        header: t('table.columns.role'),
        valueGetter: (item) => <RoleLabel role={item.role} />,
      },
      {
        header: t('table.columns.status'),
        valueGetter: (item) => <UserStatusLabel status={item.status} />,
      },
      {
        header: t('table.columns.agents'),
        multiValueRenderer: {
          itemsGetter: (item) => item.agents,
          valueGetter: (agent: AgentInfo) => (
            <div key={agent.userId} className="tw-flex">
              <span className="tw-pr-2">
                {agent.user && getAgentName(agent.user)}
              </span>
              <StylizedNumber
                value={agent.payinCompensationPercentage || 0}
                unit="%"
              />
              <span className="tw-px-1">{'/'}</span>
              <StylizedNumber
                value={agent.payoutCompensationPercentage || 0}
                unit="%"
              />
            </div>
          ),
        },
      },
      {
        header: t('table.columns.actions'),
        valueGetter: (item) => (
          <Fragment>
            <IconButton
              color="primary"
              disabled={isActionDisabled(item)}
              onClick={() => handleEdit(item)}
            >
              <ManageAccountsIcon />
            </IconButton>
            <IconButton
              color="error"
              disabled={item.email === userEmail || isActionDisabled(item)}
              onClick={() => handleRemove(item)}
            >
              <DeleteIcon />
            </IconButton>
          </Fragment>
        ),
      },
    ],
    [userEmail, handleEdit, handleRemove, isActionDisabled, getAgentName, t],
  );

  const filtersDefinitions: FilterDefinition<UsersFilters>[] = useMemo(
    () => [
      {
        label: `${t('filters.user')}`,
        type: FilterDefinitionType.User,
        name: 'userId',
        users: queryResultAllUsers.data,
      },
      {
        label: t('filters.role'),
        name: 'role',
        type: FilterDefinitionType.Enum,
        enum: UserRole,
        getDisplayName: userUtils.getRoleLabel,
      },
      {
        label: t('filters.status'),
        name: 'status',
        type: FilterDefinitionType.Enum,
        enum: UserStatus,
        getDisplayName: userUtils.getStatusLabel,
      },
      {
        label: `${t('filters.agent_compensation')} (${tCommon(
          'common.payin',
        )})`,
        type: FilterDefinitionType.Numeric,
        name: 'agentPayinCompensation',
      },
      {
        label: `${t('filters.agent_compensation')} (${tCommon(
          'common.payout',
        )})`,
        type: FilterDefinitionType.Numeric,
        name: 'agentPayoutCompensation',
      },
      {
        label: `${t('filters.agent')}`,
        type: FilterDefinitionType.Select,
        name: 'agentId',
        options: map(agents, (agent: User) => ({
          label: `${getAgentName(agent)}`,
          value: agent.id,
        })),
      },
    ],
    [agents, queryResultAllUsers, getAgentName, t, tCommon],
  );

  return (
    <Fragment>
      <CrudPage
        filters={{ filtersDefinitions }}
        table={{
          queryResult: queryResultFilteredUsers,
          paginated: true,
          ignoreState: { refetchLoading: true },
          columns,
        }}
      />
      <Dialog
        title={t('remove_dialog.title')}
        onClose={handleCloseRemoveDialog}
        {...confirmRemoveDialogProps}
      >
        <Fragment>
          {confirmRemoveDialogProps.data && (
            <div>{confirmRemoveDialogProps.data.email}</div>
          )}
        </Fragment>
      </Dialog>
    </Fragment>
  );
};
